import { doc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '@/firebase';

/**
 * Tipos de eventos que disparan push notifications
 */
export type PushEventType = 'STATUS' | 'ALERT';

/**
 * Estados de ruta válidos
 */
export type RouteStatus = 'GO' | 'SALIMOS' | 'VOLVEMOS' | 'FIN';

/**
 * Interfaz para crear un evento de push
 */
export interface CreatePushEventData {
  type: PushEventType;
  message: string;
  status?: RouteStatus;
  route: string;
  senderDeviceId: string; // deviceId del dispositivo que genera el evento
}

/**
 * Crea un documento de push event en Firestore
 * Se consume por Cloud Function para enviar notificaciones
 * IMPORTANTE: senderDeviceId se usa para excluir al dispositivo emisor
 */
export async function createPushEvent(
  workspaceId: string,
  data: CreatePushEventData
): Promise<string> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    const userId = auth.currentUser.uid;

    // Validar entrada
    if (!data.type || !data.message || !data.route || !data.senderDeviceId) {
      throw new Error('Missing required fields: type, message, route, senderDeviceId');
    }

    if (data.type === 'STATUS' && !data.status) {
      throw new Error('STATUS events require status field');
    }

    // Referencia a la colección pushEvents
    const pushEventsRef = collection(db, 'workspaces', workspaceId, 'pushEvents');

    // Documento a crear
    const eventData = {
      type: data.type,
      message: data.message,
      status: data.status || null,
      route: data.route,
      createdBy: userId,
      senderDeviceId: data.senderDeviceId, // Excluir este dispositivo en Cloud Function
      createdAt: serverTimestamp(),
      processedAt: null, // Cloud Function lo establece
    };

    // Crear documento con ID auto-generado
    const docRef = await addDoc(pushEventsRef, eventData);

    console.log('[PUSH_EVENT] ✅ Push event created:', {
      workspaceId,
      eventId: docRef.id,
      type: data.type,
      senderDeviceId: data.senderDeviceId,
      message: data.message,
    });

    return docRef.id;
  } catch (error) {
    console.error('[PUSH_EVENT] Error creating push event:', error);
    throw error;
  }
}

/**
 * Crea un push event de STATUS (cuando usuario pulsa un botón de estado)
 */
export async function createStatusPushEvent(
  workspaceId: string,
  status: RouteStatus,
  statusLabel: string,
  senderDeviceId: string,
  profileName?: string
): Promise<string> {
  const message = profileName
    ? `${profileName} está en "${statusLabel}"`
    : statusLabel;

  return createPushEvent(workspaceId, {
    type: 'STATUS',
    status,
    message,
    route: 'avisos',
    senderDeviceId,
  });
}

/**
 * Crea un push event de ALERT (cuando usuario envía una alerta)
 */
export async function createAlertPushEvent(
  workspaceId: string,
  alertMessage: string,
  senderDeviceId: string,
  profileName?: string
): Promise<string> {
  const message = profileName ? `⚠️ Alerta de ${profileName}` : alertMessage;
  return createPushEvent(workspaceId, {
    type: 'ALERT',
    message,
    route: 'avisos',
    senderDeviceId,
  });
}
